'use client';

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FocusEvent,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpRight,
  BookOpen,
  ChevronDown,
  Download,
  File,
  FileText,
  Image as ImageIcon,
  Search,
} from 'lucide-react';

import { PhotoLightbox } from '@/components/common/PhotoLightbox';
import type { AttachmentFile } from '@/components/common/worksheetPhoto';
import { cn } from '@/lib/utils';
import { documentsApi } from '@/services/documents';
import type { ChatMessage } from '@/types/chat';
import type { DocumentationDocument } from '@/types/documents';
import { safeFilename } from '@/utils/bigChangeAttachment';
import { saveAttachment, saveAttachments } from '@/utils/saveAttachment';

import {
  placePreviewHorizontally,
  placePreviewVertically,
} from '../utils/assetPreviewPlacement';
import { formatPageRange } from '../utils/libraryDocuments';
import type { ExternalResourceLink } from '../utils/toolOutput';
import { collectTurnAssets, type TurnAsset } from '../utils/turnAssets';

type PdfDialogProps = {
  file: AttachmentFile;
  initialPage?: number;
  onClose: () => void;
};

const SERVICE_LABEL: Record<ExternalResourceLink['service'], string> = {
  calendar: 'Calendar',
  gmail: 'Gmail',
  outlook: 'Outlook',
  pipedrive: 'Pipedrive',
};

const SERVICE_FAVICON: Record<ExternalResourceLink['service'], string> = {
  calendar:
    'https://www.gstatic.com/images/branding/product/1x/calendar_2020q4_48dp.png',
  gmail:
    'https://www.gstatic.com/images/branding/product/1x/gmail_2020q4_48dp.png',
  outlook:
    'https://res-1.cdn.office.net/files/fabric/assets/brand-icons/product/png/outlook_48x1.png',
  pipedrive: 'https://www.pipedrive.com/favicon.ico',
};

const rowClassName =
  'flex min-h-9 w-full min-w-0 items-center gap-2 rounded-lg px-2 text-left transition-colors duration-100 hover:bg-white/[0.06] focus-visible:bg-white/[0.06] focus-visible:outline-none';

const headerIconButtonClassName =
  'inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-fg-muted transition-colors duration-100 hover:bg-white/[0.06] hover:text-white focus-visible:bg-white/[0.06] focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40';

const PREVIEW_DELAY_MS = 150;
const IMAGE_OUTLINE = 'outline outline-1 -outline-offset-1 outline-white/10';

type DownloadableAsset = Extract<TurnAsset, { kind: 'image' | 'pdf' | 'file' }>;
type ImageAsset = Extract<TurnAsset, { kind: 'image' }>;
type LibraryAsset = Extract<TurnAsset, { kind: 'library' }>;

type PreviewableAsset = ImageAsset | LibraryAsset;

const LIBRARY_DOCUMENT_QUERY_KEY = 'connie-library-document';

/**
 * A Library Document from the Knowledge Base endpoint, which only returns the
 * member's own and shared documents. A ready one stays cached for the
 * session, so hover and open share one request.
 */
const libraryDocumentQuery = (documentId: string) => ({
  queryKey: [LIBRARY_DOCUMENT_QUERY_KEY, documentId],
  queryFn: async (): Promise<DocumentationDocument | null> => {
    const data: unknown = await documentsApi.getDocument(documentId);
    const found = Array.isArray(data) ? data[0] : data;
    return (found as DocumentationDocument | undefined) ?? null;
  },
  staleTime: (query: {
    state: { data: DocumentationDocument | null | undefined };
  }) => (query.state.data?.status === 'ready' ? Infinity : 0),
  gcTime: Infinity,
  retry: false,
});

/** Summaries arrive as light Markdown; the card shows plain text. */
const plainSummary = (text: string) =>
  text
    .replace(/\*\*|__/g, '')
    .replace(/^#+\s*/gm, '')
    .trim();

const isDownloadable = (asset: TurnAsset): asset is DownloadableAsset =>
  asset.kind === 'image' || asset.kind === 'pdf' || asset.kind === 'file';

type PreviewState = {
  id: string;
  label: string;
  left: number;
  width: number;
  /** The hovered row and the dialog (or viewport) it sits in. */
  anchor: { top: number; bottom: number };
  bounds: { top: number; bottom: number };
} & (
  | { kind: 'image'; photo: ImageAsset['photo'] }
  | { kind: 'library'; documentId: string }
);

type PhotoPreviewState = Extract<PreviewState, { kind: 'image' }>;
type LibraryPreviewState = Extract<PreviewState, { kind: 'library' }>;

const previewCardClassName =
  'pointer-events-none fixed z-[2000] rounded-2xl bg-card p-2 shadow-[0_8px_24px_rgb(0_0_0/0.28),0_2px_6px_rgb(0_0_0/0.18)] motion-safe:transition-opacity motion-safe:duration-150 motion-safe:ease-out';

/**
 * The card's top from its measured height, so its bottom edge sits just above
 * the row whatever it shows. Measured before paint and again when it resizes
 * (a photo or summary loading); hidden until the first measurement.
 */
const usePreviewTop = (preview: PreviewState) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => setHeight(node.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const top =
    height === null
      ? null
      : placePreviewVertically(preview.anchor, preview.bounds, height);
  return { ref, top };
};

const useFadeIn = () => {
  const [visible, setVisible] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return visible;
};

const statusLabel = (asset: TurnAsset) => {
  switch (asset.kind) {
    case 'image':
      return 'Photo';
    case 'pdf':
      return 'PDF';
    case 'file':
      return 'File';
    case 'link':
      return SERVICE_LABEL[asset.service];
    case 'library':
      return asset.pages.length
        ? `Library · ${formatPageRange(asset.pages)}`
        : 'Library';
    default: {
      const _exhaustive: never = asset;
      return _exhaustive;
    }
  }
};

const AssetGlyph = ({ asset }: { asset: TurnAsset }) => {
  switch (asset.kind) {
    case 'image':
      return (
        <ImageIcon
          size={14}
          strokeWidth={2}
          className='shrink-0 text-white/55'
        />
      );
    case 'pdf':
      return (
        <FileText
          size={14}
          strokeWidth={2}
          className='shrink-0 text-white/55'
        />
      );
    case 'file':
      return (
        <File size={14} strokeWidth={2} className='shrink-0 text-white/55' />
      );
    case 'link':
      return (
        <img
          src={SERVICE_FAVICON[asset.service]}
          alt=''
          width={14}
          height={14}
          draggable={false}
          className='size-3.5 shrink-0'
        />
      );
    case 'library':
      return (
        <BookOpen
          size={14}
          strokeWidth={2}
          className='shrink-0 text-white/55'
        />
      );
    default: {
      const _exhaustive: never = asset;
      return _exhaustive;
    }
  }
};

const AssetBody = ({
  asset,
  status,
}: {
  asset: TurnAsset;
  status?: string;
}) => (
  <>
    <AssetGlyph asset={asset} />
    <span
      title={asset.label}
      className='min-w-0 flex-1 truncate text-[13px] font-medium text-white'
    >
      {asset.label}
    </span>
    <span className='shrink-0 text-[12px] text-fg-muted tabular-nums'>
      {status ?? statusLabel(asset)}
    </span>
    {asset.kind === 'link' ? (
      <ArrowUpRight
        size={14}
        strokeWidth={2}
        className='shrink-0 text-fg-muted'
      />
    ) : null}
  </>
);

const extensionFrom = (url: string) => {
  try {
    const base = new URL(url).pathname.split('/').pop() ?? '';
    return base.match(/(\.[a-z0-9]{2,5})$/i)?.[1]?.toLowerCase() ?? '';
  } catch {
    return '';
  }
};

const assetUrl = (asset: DownloadableAsset) =>
  asset.kind === 'image' ? asset.photo.url : asset.file.url;

const downloadFilename = (asset: DownloadableAsset) => {
  const url = assetUrl(asset);
  const fallback =
    asset.kind === 'pdf' ? '.pdf' : asset.kind === 'image' ? '.jpg' : '';
  const extension = extensionFrom(url) || fallback;
  const stem = asset.label.replace(/\s+/g, ' ').trim() || 'download';
  const named = stem.toLowerCase().endsWith(extension)
    ? stem
    : `${stem}${extension}`;
  return safeFilename(named) ?? `download${extension}`;
};

const viewportBounds = () =>
  new DOMRect(0, 0, window.innerWidth, window.innerHeight);

const DownloadAssetButton = ({
  asset,
  onError,
}: {
  asset: DownloadableAsset;
  onError: (label: string) => void;
}) => {
  const url = assetUrl(asset);

  return (
    <button
      type='button'
      data-cy='turn-asset-download'
      aria-label={`Download ${asset.label}`}
      title='Download'
      className='inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors duration-100 hover:bg-white/[0.08] hover:text-white focus-visible:bg-white/[0.08] focus-visible:outline-none'
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        void saveAttachment(url, downloadFilename(asset)).catch(() => {
          onError(asset.label);
        });
      }}
    >
      <Download size={14} strokeWidth={2} />
    </button>
  );
};

const staysInside = (container: Node, next: EventTarget | null) =>
  next instanceof Node && container.contains(next);

const AssetPhotoPreview = ({ preview }: { preview: PhotoPreviewState }) => {
  const [src, setSrc] = useState(
    preview.photo.thumbnailUrl || preview.photo.url
  );
  const visible = useFadeIn();
  const { ref, top } = usePreviewTop(preview);
  const [failed, setFailed] = useState(false);

  if (failed || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={ref}
      data-cy='turn-asset-photo-preview'
      aria-hidden
      className={previewCardClassName}
      style={{
        top: top ?? 0,
        left: preview.left,
        width: preview.width,
        opacity: visible ? 1 : 0,
        visibility: top === null ? 'hidden' : undefined,
      }}
    >
      <img
        src={src}
        alt=''
        draggable={false}
        onError={() => {
          if (src !== preview.photo.url) {
            setSrc(preview.photo.url);
            return;
          }
          setFailed(true);
        }}
        className={cn(
          'max-h-40 w-full rounded-lg bg-white/[0.04] object-contain',
          IMAGE_OUTLINE
        )}
      />
      <p className='line-clamp-2 px-1 pt-2 text-xs leading-5 text-fg-muted'>
        {preview.label}
      </p>
    </div>,
    document.body
  );
};

const LibraryDocumentPreview = ({
  preview,
}: {
  preview: LibraryPreviewState;
}) => {
  const visible = useFadeIn();
  const { ref, top } = usePreviewTop(preview);
  const { data, isPending, isError } = useQuery(
    libraryDocumentQuery(preview.documentId)
  );

  if (typeof document === 'undefined') return null;

  const summary = plainSummary(data?.summary || data?.description || '');
  const category = data?.category?.trim();
  let body = summary;
  if (!summary) {
    if (isPending) body = 'Loading summary…';
    else if (isError || !data) body = "Couldn't load the summary";
    else if (data.status === 'processing') body = 'Still processing';
    else body = 'No summary yet';
  }

  return createPortal(
    <div
      ref={ref}
      data-cy='turn-asset-library-preview'
      aria-hidden
      className={previewCardClassName}
      style={{
        top: top ?? 0,
        left: preview.left,
        width: preview.width,
        opacity: visible ? 1 : 0,
        visibility: top === null ? 'hidden' : undefined,
      }}
    >
      <div className='flex max-h-52 flex-col gap-1 overflow-hidden px-1 py-0.5'>
        <p className='line-clamp-2 text-[13px] font-medium leading-5 text-white'>
          {data?.name || preview.label}
        </p>
        {category ? (
          <p className='truncate text-xs leading-5 text-fg-muted'>{category}</p>
        ) : null}
        <p
          className={cn(
            'line-clamp-6 text-xs leading-5 text-pretty',
            summary ? 'text-white/80' : 'text-fg-muted'
          )}
        >
          {body}
        </p>
      </div>
    </div>,
    document.body
  );
};

/** Hover after a short delay, or keyboard focus at once, shows a preview card. */
const previewHandlers = (
  asset: PreviewableAsset,
  onPreview: (
    asset: PreviewableAsset,
    row: HTMLElement,
    immediate: boolean
  ) => void,
  onHidePreview: (id: string) => void
) => ({
  onMouseEnter: (event: MouseEvent<HTMLElement>) =>
    onPreview(asset, event.currentTarget, false),
  onMouseLeave: (event: MouseEvent<HTMLElement>) => {
    if (staysInside(event.currentTarget, event.relatedTarget)) return;
    if (event.currentTarget.contains(document.activeElement)) return;
    onHidePreview(asset.id);
  },
  onFocus: (event: FocusEvent<HTMLElement>) => {
    if (
      !(event.target instanceof HTMLElement) ||
      !event.target.matches(':focus-visible')
    ) {
      return;
    }
    onPreview(asset, event.currentTarget, true);
  },
  onBlur: (event: FocusEvent<HTMLElement>) => {
    if (staysInside(event.currentTarget, event.relatedTarget)) return;
    onHidePreview(asset.id);
  },
});

const AssetRow = ({
  asset,
  libraryStatus,
  onOpenImage,
  onOpenPdf,
  onOpenLibrary,
  onPreview,
  onHidePreview,
  onDownloadError,
}: {
  asset: TurnAsset;
  libraryStatus?: string;
  onOpenImage: (asset: ImageAsset) => void;
  onOpenPdf: (asset: Extract<TurnAsset, { kind: 'pdf' }>) => void;
  onOpenLibrary: (asset: LibraryAsset) => void;
  onPreview: (
    asset: PreviewableAsset,
    row: HTMLElement,
    immediate: boolean
  ) => void;
  onHidePreview: (id: string) => void;
  onDownloadError: (label: string) => void;
}) => {
  switch (asset.kind) {
    case 'link':
      return (
        <a
          href={asset.href}
          target='_blank'
          rel='noopener noreferrer'
          data-cy='turn-asset-row'
          data-kind={asset.kind}
          className={rowClassName}
        >
          <AssetBody asset={asset} />
        </a>
      );
    case 'file':
      return (
        <div
          className={rowClassName}
          data-cy='turn-asset-row'
          data-kind={asset.kind}
        >
          <a
            href={asset.file.url}
            target='_blank'
            rel='noopener noreferrer'
            className='flex min-w-0 flex-1 items-center gap-2'
          >
            <AssetBody asset={asset} />
          </a>
          <DownloadAssetButton asset={asset} onError={onDownloadError} />
        </div>
      );
    case 'pdf':
      return (
        <div
          className={rowClassName}
          data-cy='turn-asset-row'
          data-kind={asset.kind}
        >
          <button
            type='button'
            className='flex min-w-0 flex-1 items-center gap-2 text-left'
            onClick={() => onOpenPdf(asset)}
          >
            <AssetBody asset={asset} />
          </button>
          <DownloadAssetButton asset={asset} onError={onDownloadError} />
        </div>
      );
    case 'library':
      return (
        <div
          className={rowClassName}
          data-cy='turn-asset-row'
          data-kind={asset.kind}
          {...previewHandlers(asset, onPreview, onHidePreview)}
        >
          <button
            type='button'
            className='flex min-w-0 flex-1 items-center gap-2 text-left'
            onClick={() => {
              onHidePreview(asset.id);
              onOpenLibrary(asset);
            }}
          >
            <AssetBody asset={asset} status={libraryStatus} />
          </button>
          <a
            href={asset.href}
            target='_blank'
            rel='noopener noreferrer'
            data-cy='turn-asset-library-link'
            aria-label={`Open the chat for ${asset.label} in Knowledge Base, opens in a new tab`}
            title='Open document chat'
            className='inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors duration-100 hover:bg-white/[0.08] hover:text-white focus-visible:bg-white/[0.08] focus-visible:outline-none'
          >
            <ArrowUpRight size={14} strokeWidth={2} />
          </a>
        </div>
      );
    case 'image':
      return (
        <div
          className={rowClassName}
          data-cy='turn-asset-row'
          data-kind={asset.kind}
          {...previewHandlers(asset, onPreview, onHidePreview)}
        >
          <button
            type='button'
            className='flex min-w-0 flex-1 items-center gap-2 text-left'
            onClick={() => onOpenImage(asset)}
          >
            <AssetBody asset={asset} />
          </button>
          <DownloadAssetButton asset={asset} onError={onDownloadError} />
        </div>
      );
    default: {
      const _exhaustive: never = asset;
      return _exhaustive;
    }
  }
};

export function TurnAssetDrawer({ messages }: { messages: ChatMessage[] }) {
  const assets = useMemo(() => collectTurnAssets(messages), [messages]);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [photoIndex, setPhotoIndex] = useState<number | null>(null);
  const [pdfFile, setPdfFile] = useState<AttachmentFile | null>(null);
  const queryClient = useQueryClient();
  const [pdfPage, setPdfPage] = useState<number | undefined>();
  const [libraryStatus, setLibraryStatus] = useState<
    Record<string, string | undefined>
  >({});
  const [PdfDialog, setPdfDialog] =
    useState<ComponentType<PdfDialogProps> | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadNote, setDownloadNote] = useState('');
  const canHoverRef = useRef(false);
  const previewTimer = useRef<number | null>(null);
  const downloadingRef = useRef(false);
  const aliveRef = useRef(true);
  const openRef = useRef(open);
  openRef.current = open;

  const photos = useMemo(
    () =>
      assets.flatMap(asset => (asset.kind === 'image' ? [asset.photo] : [])),
    [assets]
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return assets;
    return assets.filter(asset => {
      const haystack = `${asset.label} ${statusLabel(asset)}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [assets, query]);

  const downloadable = useMemo(() => assets.filter(isDownloadable), [assets]);

  useEffect(() => {
    aliveRef.current = true;
    const media = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => {
      canHoverRef.current = media.matches;
    };
    update();
    media.addEventListener('change', update);
    return () => {
      aliveRef.current = false;
      media.removeEventListener('change', update);
      if (previewTimer.current !== null) {
        window.clearTimeout(previewTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!preview) return;
    if (
      !open ||
      photoIndex !== null ||
      pdfFile ||
      !visible.some(asset => asset.id === preview.id)
    ) {
      setPreview(null);
    }
  }, [preview, open, photoIndex, pdfFile, visible]);

  useEffect(() => {
    if (!preview) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setPreview(null);
    };
    const hide = () => setPreview(null);
    window.addEventListener('keydown', dismiss, true);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('keydown', dismiss, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [preview]);

  useEffect(() => {
    if (!pdfFile || PdfDialog) return;
    let cancelled = false;
    // pdf.js reads DOMMatrix at import time, which the server does not have.
    import('@/components/common/AttachmentPdfDialog').then(mod => {
      if (!cancelled) setPdfDialog(() => mod.AttachmentPdfDialog);
    });
    return () => {
      cancelled = true;
    };
  }, [pdfFile, PdfDialog]);

  const clearPreviewTimer = () => {
    if (previewTimer.current === null) return;
    window.clearTimeout(previewTimer.current);
    previewTimer.current = null;
  };

  const hidePreview = (id?: string) => {
    clearPreviewTimer();
    setPreview(current => {
      if (!current) return null;
      if (id && current.id !== id) return current;
      return null;
    });
  };

  const showPreview = (
    asset: PreviewableAsset,
    row: HTMLElement,
    immediate: boolean
  ) => {
    if (!openRef.current) return;
    if (!immediate && !canHoverRef.current) return;
    clearPreviewTimer();
    const reveal = () => {
      const rect = row.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const dialog = row.closest('.chakra-modal__content');
      const bounds = dialog?.getBoundingClientRect() ?? viewportBounds();
      const placement = {
        ...placePreviewHorizontally(rect, bounds),
        anchor: { top: rect.top, bottom: rect.bottom },
        bounds: { top: bounds.top, bottom: bounds.bottom },
      };
      setPreview(
        asset.kind === 'image'
          ? {
              kind: 'image',
              id: asset.id,
              photo: asset.photo,
              label: asset.label,
              ...placement,
            }
          : {
              kind: 'library',
              id: asset.id,
              documentId: asset.documentId,
              label: asset.label,
              ...placement,
            }
      );
    };
    if (immediate) {
      reveal();
      return;
    }
    previewTimer.current = window.setTimeout(reveal, PREVIEW_DELAY_MS);
  };

  /**
   * The file URL comes from the same Knowledge Base endpoint the document
   * page uses, which only returns the member's own and shared documents.
   */
  const openLibraryDocument = (asset: LibraryAsset) => {
    if (libraryStatus[asset.id] === 'Opening…') return;
    setLibraryStatus(current => ({ ...current, [asset.id]: 'Opening…' }));
    const settle = (status?: string) => {
      if (!aliveRef.current) return;
      setLibraryStatus(current => ({ ...current, [asset.id]: status }));
    };
    queryClient
      .fetchQuery(libraryDocumentQuery(asset.documentId))
      .then(libraryDocument => {
        const url = libraryDocument?.file?.url;
        if (!url) {
          settle("Couldn't open");
          return;
        }
        settle();
        if (!aliveRef.current) return;
        setPdfPage(asset.pages[0]);
        setPdfFile({ url, kind: 'pdf', label: asset.label });
      })
      .catch(() => settle("Couldn't open"));
  };

  const downloadAll = () => {
    if (!downloadable.length || downloadingRef.current) return;
    downloadingRef.current = true;
    setDownloading(true);
    const total = downloadable.length;
    const queuedLabel = total === 1 ? '1 file' : `${total} files`;
    setDownloadNote(`Downloading ${queuedLabel}`);
    const finish = (note: string) => {
      if (!aliveRef.current) return;
      downloadingRef.current = false;
      setDownloading(false);
      setDownloadNote(note);
    };
    const files = downloadable.map(asset => ({
      url: assetUrl(asset),
      filename: downloadFilename(asset),
    }));

    if (files.length === 1) {
      void saveAttachment(files[0].url, files[0].filename)
        .then(() => finish('1 file sent to downloads'))
        .catch(() => finish("Couldn't save 1 file"));
      return;
    }

    void saveAttachments(files, 'connie-assets.zip')
      .then(result => {
        finish(
          result.failed === 0
            ? `Saved ${result.saved} files as a zip`
            : `Saved ${result.saved} of ${total} files as a zip`
        );
      })
      .catch(() => finish(`Couldn't save ${queuedLabel}`));
  };

  if (!assets.length) return null;

  const countLabel = `${assets.length} Asset${assets.length === 1 ? '' : 's'}`;

  return (
    <div
      data-cy='turn-asset-drawer'
      className='mb-2 rounded-xl bg-white/[0.04] p-1 ring-1 ring-inset ring-white/[0.08]'
    >
      <div className='flex items-center gap-1'>
        <button
          type='button'
          aria-expanded={open}
          onClick={() => setOpen(current => !current)}
          className='flex min-h-9 min-w-0 flex-1 items-center gap-1.5 rounded-lg px-1.5 text-left transition-colors duration-100 hover:bg-white/[0.06] focus-visible:bg-white/[0.06] focus-visible:outline-none'
        >
          <ChevronDown
            size={14}
            strokeWidth={2}
            className='shrink-0 text-fg-muted transition-transform duration-200 motion-reduce:transition-none'
            style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
          />
          <span className='text-[13px] text-fg-muted tabular-nums'>
            {countLabel}
          </span>
        </button>
        {downloadable.length ? (
          <button
            type='button'
            data-cy='turn-asset-download-all'
            aria-label='Download all assets'
            title='Download all'
            aria-busy={downloading || undefined}
            disabled={downloading}
            onClick={downloadAll}
            className={headerIconButtonClassName}
          >
            <Download size={14} strokeWidth={2} />
          </button>
        ) : null}
        <button
          type='button'
          aria-label='Search assets'
          aria-expanded={searchOpen}
          onClick={() => {
            setSearchOpen(current => !current);
            setOpen(true);
          }}
          className={cn(
            headerIconButtonClassName,
            searchOpen && 'bg-white/[0.06] text-white'
          )}
        >
          <Search size={14} strokeWidth={2} />
        </button>
      </div>

      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300 motion-reduce:transition-none',
          !open && 'pointer-events-none'
        )}
        aria-hidden={!open}
        style={{
          gridTemplateRows: open ? '1fr' : '0fr',
          opacity: open ? 1 : 0,
        }}
      >
        <div className='min-h-0 overflow-hidden'>
          {searchOpen ? (
            <div className='px-1.5 pb-1'>
              <input
                type='search'
                value={query}
                autoFocus
                aria-label='Search assets'
                placeholder='Search assets'
                onChange={event => setQuery(event.target.value)}
                onKeyDown={event => {
                  if (event.key !== 'Escape') return;
                  setQuery('');
                  setSearchOpen(false);
                }}
                className='h-9 w-full rounded-lg bg-white/[0.06] px-2.5 text-base text-white outline-none ring-1 ring-inset ring-white/[0.08] placeholder:text-fg-muted sm:text-sm'
              />
            </div>
          ) : null}
          {visible.length ? (
            <div
              role='list'
              aria-label='Assets from this chat'
              className='max-h-48 overflow-y-auto overscroll-contain'
            >
              {visible.map(asset => (
                <div key={asset.id} role='listitem'>
                  <AssetRow
                    asset={asset}
                    libraryStatus={libraryStatus[asset.id]}
                    onOpenImage={image => {
                      hidePreview();
                      const index = photos.findIndex(
                        photo => photo.url === image.photo.url
                      );
                      setPhotoIndex(index >= 0 ? index : 0);
                    }}
                    onOpenPdf={pdf => {
                      setPdfPage(undefined);
                      setPdfFile(pdf.file);
                    }}
                    onOpenLibrary={openLibraryDocument}
                    onPreview={showPreview}
                    onHidePreview={hidePreview}
                    onDownloadError={label => {
                      setDownloadNote(`Couldn't save ${label}`);
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className='px-3 py-2 text-[13px] text-fg-muted'>
              No matching assets
            </p>
          )}
        </div>
      </div>

      {photoIndex !== null && photos[photoIndex] ? (
        <PhotoLightbox
          photos={photos}
          index={photoIndex}
          onIndex={setPhotoIndex}
          onClose={() => setPhotoIndex(null)}
        />
      ) : null}
      {pdfFile && PdfDialog ? (
        <PdfDialog
          file={pdfFile}
          initialPage={pdfPage}
          onClose={() => setPdfFile(null)}
        />
      ) : null}
      <p role='status' className='sr-only'>
        {downloadNote}
      </p>
      {preview ? (
        preview.kind === 'image' ? (
          <AssetPhotoPreview key={preview.id} preview={preview} />
        ) : (
          <LibraryDocumentPreview key={preview.id} preview={preview} />
        )
      ) : null}
    </div>
  );
}
